import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";
import { formatLapTime, formatSetupName } from "../utils/formatters";
import ConfirmModal from "../components/ConfirmModal";

// Map a week's iRacing series to the checklist car id (matches PitAdjustments).
function carIdForSeries(seriesName: string | null | undefined): string | null {
  const s = (seriesName || "").toLowerCase();
  if (s.includes("class c") || s.includes("truck")) return "nascar-trucks";
  if (s.includes("class b") || s.includes("xfinity") || s.includes("o'reilly") || s.includes("oreilly")) return "nascar-oreilly";
  if (s.includes("class a") || s.includes("cup")) return "nascar-cup-next-gen";
  return null;
}
const CAR_LABEL: Record<string, string> = {
  "nascar-trucks": "NASCAR Trucks",
  "nascar-oreilly": "O'Reilly (Xfinity)",
  "nascar-cup-next-gen": "Cup Next Gen",
};

type Status = "untested" | "incomplete" | "complete";
interface Item {
  ref: string;
  label: string;
  guidance: string | null;
  rule: unknown;
  status: Status;
  driverId: string | null;
  setupRunIds: string[];
  notes: string | null;
}
interface Stage { label: string; items: Item[]; complete: number; total: number }
interface ChecklistRun {
  id: string;
  teamId: string;
  trackWeekId: string | null;
  carId: string;
  templateName: string | null;
  stages: Stage[];
  complete: number;
  total: number;
}
interface TemplateSummary { id: string; name: string; version: number }
interface Member { userId: string; displayName: string | null }
interface WeekRun { id: string; displayName: string | null; setupName: string | null; bestLapTime: number | null }

const STATUS_OPTS: { value: Status; label: string; color: string }[] = [
  { value: "untested", label: "Untested", color: "var(--text-muted)" },
  { value: "incomplete", label: "Testing", color: "var(--flag-yellow)" },
  { value: "complete", label: "Done", color: "var(--flag-green)" },
];

export default function Checklist() {
  const { teamId, trackWeekId } = useParams<{ teamId: string; trackWeekId: string }>();
  const trackWeeks = useRunStore((s) => s.trackWeeks);
  const seriesName = useMemo(
    () => trackWeeks.find((w) => w.id === trackWeekId)?.seriesName ?? null,
    [trackWeeks, trackWeekId],
  );
  const carId = carIdForSeries(seriesName);
  const isAdmin = useTeamStore((s) => s.activeTeam?.myRole === "owner" || s.activeTeam?.myRole === "admin");

  const [run, setRun] = useState<ChecklistRun | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [weekRuns, setWeekRuns] = useState<WeekRun[]>([]);
  const [pickTemplate, setPickTemplate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<"reset" | "delete" | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = useCallback(async () => {
    if (!teamId || !trackWeekId || !carId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await api<ChecklistRun>(
        `/api/checklist/runs?team_id=${teamId}&car_id=${carId}&track_week_id=${trackWeekId}`,
      );
      setRun(r);
    } catch {
      // No instance yet — load the team's templates for this car to start from.
      setRun(null);
      try {
        const tpls = await api<TemplateSummary[]>(`/api/checklist/templates?team_id=${teamId}&car_id=${carId}`);
        setTemplates(tpls);
        if (tpls.length) setPickTemplate(tpls[0].id);
      } catch { setTemplates([]); }
    } finally {
      setLoading(false);
    }
  }, [teamId, trackWeekId, carId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!teamId) return;
    api<Member[]>(`/api/teams/${teamId}/members`).then(setMembers).catch(() => setMembers([]));
  }, [teamId]);

  // This team's runs for this week — the only runs offerable as setup-version proof
  // (the endpoint is team-gated, so no cross-team enumeration).
  useEffect(() => {
    if (!teamId || !trackWeekId) return;
    api<WeekRun[]>(`/api/runs?team_id=${teamId}&track_week_id=${trackWeekId}&limit=100`).then(setWeekRuns).catch(() => setWeekRuns([]));
  }, [teamId, trackWeekId]);

  const runLabel = useCallback((rid: string): string => {
    const r = weekRuns.find((x) => x.id === rid);
    if (!r) return `Run ${rid.slice(0, 6)}`;
    return [r.displayName || "Driver", r.setupName ? formatSetupName(r.setupName) : null, r.bestLapTime != null ? formatLapTime(r.bestLapTime) : null]
      .filter(Boolean).join(" · ");
  }, [weekRuns]);

  const start = useCallback(async () => {
    if (!teamId || !carId || !pickTemplate) return;
    setLoading(true);
    try {
      const r = await api<ChecklistRun>("/api/checklist/runs", {
        method: "POST",
        body: JSON.stringify({ teamId, trackWeekId: trackWeekId || null, carId, templateId: pickTemplate }),
      });
      setRun(r);
    } catch (e: any) {
      setError(e?.message || "Could not start the checklist");
    } finally {
      setLoading(false);
    }
  }, [teamId, carId, trackWeekId, pickTemplate]);

  const resetRun = useCallback(async () => {
    if (!run) return;
    setConfirmBusy(true);
    try { setRun(await api<ChecklistRun>(`/api/checklist/runs/${run.id}/reset`, { method: "POST" })); }
    catch (e: any) { setError(e?.message || "Could not reset"); }
    finally { setConfirmBusy(false); setConfirmAction(null); }
  }, [run]);

  const deleteRun = useCallback(async () => {
    if (!run) return;
    setConfirmBusy(true);
    try { await api(`/api/checklist/runs/${run.id}`, { method: "DELETE" }); setRun(null); await load(); }
    catch (e: any) { setError(e?.message || "Could not delete"); }
    finally { setConfirmBusy(false); setConfirmAction(null); }
  }, [run, load]);

  const patch = useCallback(async (ref: string, body: Record<string, unknown>) => {
    if (!run) return;
    try {
      const r = await api<ChecklistRun>(`/api/checklist/runs/${run.id}/items/${ref}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setRun(r);
    } catch (e: any) {
      setError(e?.message || "Could not save");
    }
  }, [run]);

  if (!carId) {
    return <div style={st.empty}>This series isn’t one of the checklist cars yet (Trucks, O’Reilly, Cup).</div>;
  }
  if (loading) {
    return <div style={st.empty}><span className="spinner" /> Loading checklist…</div>;
  }

  // No instance — offer to start from a template.
  if (!run) {
    return (
      <div className="fade-in" style={{ maxWidth: 720 }}>
        <span className="eyebrow">Setup checklist · {CAR_LABEL[carId] || carId}</span>
        {templates.length === 0 ? (
          <div className="card" style={st.startCard}>
            <div style={st.startTitle}>No checklist template for {CAR_LABEL[carId] || carId} yet.</div>
            <p style={st.startBody}>
              {isAdmin ? (
                <>Build your team’s pre-track checklist once in{" "}
                <Link to={`/teams/${teamId}/checklists`} style={{ color: "var(--live)" }}>Checklist templates</Link>, then start it here each week.</>
              ) : (
                <>Your team hasn’t set up a {CAR_LABEL[carId] || carId} checklist yet. Ask a team admin to create one.</>
              )}
            </p>
          </div>
        ) : (
          <div className="card" style={st.startCard}>
            <div style={st.startTitle}>Start this week’s checklist</div>
            <p style={st.startBody}>Start from your {CAR_LABEL[carId] || carId} template. Its items are frozen onto this week, so later edits to the template won’t rewrite your record.</p>
            <div style={st.startRow}>
              <select className="select" value={pickTemplate} onChange={(e) => setPickTemplate(e.target.value)}>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button className="btn btn-primary" onClick={start} disabled={loading}>Start checklist</button>
            </div>
          </div>
        )}
        {error && <div style={st.error}>{error}</div>}
      </div>
    );
  }

  const cleared = run.total > 0 && run.complete === run.total;
  const pylonColor = cleared ? "var(--flag-green)" : run.complete > 0 ? "var(--flag-yellow)" : "var(--flag-red)";

  return (
    <div className="fade-in" style={{ maxWidth: 760 }}>
      <span className="eyebrow">Setup checklist · {CAR_LABEL[carId] || carId}</span>

      {/* Readiness pylon */}
      <div className="card" style={{ ...st.pylon, borderColor: pylonColor }}>
        <div style={{ ...st.pylonRail, background: pylonColor }} />
        <div style={st.pylonBody}>
          <div style={{ ...st.pylonWord, color: pylonColor }}>{cleared ? "Car cleared" : "Not cleared"}</div>
          <div style={st.pylonSub}>
            <span className="num">{run.complete}</span> of <span className="num">{run.total}</span> verified
            {run.templateName ? ` · ${run.templateName}` : ""}
          </div>
        </div>
      </div>

      {isAdmin && (
        <div style={st.adminRow}>
          <Link to={`/teams/${teamId}/checklists`} style={st.adminLink}>Edit template</Link>
          <span style={st.adminSep}>·</span>
          <button type="button" style={st.adminBtn} onClick={() => setConfirmAction("reset")}>Reset to untested</button>
          <span style={st.adminSep}>·</span>
          <button type="button" style={{ ...st.adminBtn, color: "var(--flag-red)" }} onClick={() => setConfirmAction("delete")}>Delete checklist</button>
        </div>
      )}

      {error && <div style={st.error}>{error}</div>}

      {run.stages.map((stage, si) => (
        <section key={si} style={st.stage}>
          <div style={st.stageHead}>
            <span style={st.stageLabel}>{stage.label}</span>
            <span className="num" style={st.stageCount}>{stage.complete}/{stage.total}</span>
          </div>
          {stage.items.map((item) => {
            const open = openItem === item.ref;
            return (
              <div key={item.ref} className="card" style={st.item}>
                <div style={st.itemTop}>
                  <div style={st.segment}>
                    {STATUS_OPTS.map((o) => {
                      const active = item.status === o.value;
                      return (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => patch(item.ref, { status: o.value })}
                          style={{
                            ...st.seg,
                            color: active ? "#0b0e13" : o.color,
                            background: active ? o.color : "transparent",
                            fontWeight: active ? 700 : 500,
                          }}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                  <span style={st.itemLabel}>{item.label}</span>
                  <button type="button" style={st.expand} onClick={() => setOpenItem(open ? null : item.ref)}>
                    {open ? "Hide" : "Detail"}
                  </button>
                </div>

                {open && (
                  <div style={st.detail}>
                    <div style={item.guidance ? st.guidance : st.guidanceEmpty}>
                      {item.guidance || "No guidance added for this item."}
                    </div>
                    <div style={st.detailRow}>
                      <label style={st.detailLabel}>Driver</label>
                      <select
                        className="select"
                        value={item.driverId || ""}
                        onChange={(e) => patch(item.ref, e.target.value ? { driverId: e.target.value } : { clearDriver: true })}
                        style={{ flex: 1 }}
                      >
                        <option value="">—</option>
                        {members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName || "Driver"}</option>)}
                      </select>
                    </div>

                    <div>
                      <label style={st.detailLabel}>Setups that proved this</label>
                      <div style={st.chips}>
                        {(item.setupRunIds || []).map((rid) => (
                          <span key={rid} style={st.chip}>
                            <Link to={`/runs/${rid}`} style={st.chipLink} title="Open run">{runLabel(rid)}</Link>
                            <button type="button" title="Remove setup" aria-label="Remove setup" style={st.chipX}
                              onClick={() => patch(item.ref, { setupRunIds: (item.setupRunIds || []).filter((x) => x !== rid) })}>×</button>
                          </span>
                        ))}
                        {weekRuns.filter((r) => !(item.setupRunIds || []).includes(r.id)).length > 0 ? (
                          <select className="select" value="" style={st.addSetup}
                            onChange={(e) => { if (e.target.value) patch(item.ref, { setupRunIds: [...(item.setupRunIds || []), e.target.value] }); }}>
                            <option value="">+ Attach setup…</option>
                            {weekRuns.filter((r) => !(item.setupRunIds || []).includes(r.id)).map((r) => (
                              <option key={r.id} value={r.id}>{runLabel(r.id)}</option>
                            ))}
                          </select>
                        ) : (item.setupRunIds || []).length === 0 ? (
                          <span style={st.noRuns}>{weekRuns.length === 0 ? "No runs uploaded for this week yet." : "All runs attached."}</span>
                        ) : null}
                      </div>
                    </div>

                    <label style={st.detailLabel}>Notes</label>
                    <textarea
                      key={`notes-${item.ref}-${item.notes ?? ""}`}
                      className="input"
                      defaultValue={item.notes || ""}
                      placeholder="Final setting, driver feedback, the run that confirmed it…"
                      rows={2}
                      onBlur={(e) => { if (e.target.value !== (item.notes || "")) patch(item.ref, { notes: e.target.value }); }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </section>
      ))}

      <ConfirmModal
        open={confirmAction === "reset"}
        title="Reset checklist?"
        body="Every item goes back to untested. Notes and driver assignments are kept."
        confirmLabel="Reset checklist"
        tone="danger"
        loading={confirmBusy}
        onConfirm={() => void resetRun()}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmModal
        open={confirmAction === "delete"}
        title="Delete this week's checklist?"
        body="This removes the checklist and its progress for this week. You can start a fresh one from the template."
        confirmLabel="Delete checklist"
        tone="danger"
        loading={confirmBusy}
        onConfirm={() => void deleteRun()}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  empty: { padding: "2rem", color: "var(--text-secondary)", fontSize: "0.95rem" },
  startCard: { padding: "1.5rem", marginTop: "0.75rem" },
  startTitle: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.05rem", color: "var(--text-strong)", marginBottom: "0.5rem" },
  startBody: { color: "var(--text-secondary)", fontSize: "0.9rem", lineHeight: 1.6, marginBottom: "1rem" },
  startRow: { display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" },
  error: { marginTop: "0.85rem", padding: "0.6rem 0.8rem", background: "var(--flag-red-soft)", border: "1px solid var(--flag-red)", color: "var(--flag-red)", borderRadius: "var(--r-md)", fontSize: "0.85rem" },
  pylon: { position: "relative", display: "flex", padding: "1.1rem 1.25rem 1.1rem 1.4rem", marginTop: "0.75rem", overflow: "hidden" },
  pylonRail: { position: "absolute", left: 0, top: 0, bottom: 0, width: "4px" },
  pylonBody: { display: "flex", flexDirection: "column", gap: "0.2rem" },
  pylonWord: { fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.05 },
  pylonSub: { color: "var(--text-secondary)", fontSize: "0.85rem" },
  stage: { marginTop: "1.5rem" },
  stageHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", paddingBottom: "0.5rem", borderBottom: "1px solid var(--rule)", marginBottom: "0.75rem" },
  stageLabel: { fontFamily: "var(--font-display)", fontSize: "1.05rem", fontWeight: 700, color: "var(--text-strong)" },
  stageCount: { fontSize: "0.85rem", color: "var(--text-secondary)", fontWeight: 600 },
  item: { padding: "0.85rem 1rem", marginBottom: "0.6rem" },
  itemTop: { display: "flex", alignItems: "center", gap: "0.85rem", flexWrap: "wrap" },
  segment: { display: "inline-flex", border: "1px solid var(--rule)", borderRadius: "var(--r-md)", overflow: "hidden", flexShrink: 0 },
  seg: { padding: "0.3rem 0.7rem", fontSize: "0.8125rem", border: "none", cursor: "pointer", fontFamily: "var(--font-body)" },
  itemLabel: { flex: 1, minWidth: "8rem", color: "var(--text-strong)", fontSize: "0.92rem", fontWeight: 500 },
  expand: { background: "none", border: "none", color: "var(--text-secondary)", fontSize: "0.8125rem", cursor: "pointer", padding: "0.2rem 0.3rem" },
  detail: { marginTop: "0.85rem", paddingTop: "0.85rem", borderTop: "1px solid var(--rule-faint)", display: "flex", flexDirection: "column", gap: "0.6rem" },
  guidance: { color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.55, background: "var(--ink-1)", border: "1px solid var(--rule-faint)", borderRadius: "var(--r-md)", padding: "0.6rem 0.75rem" },
  guidanceEmpty: { color: "var(--text-muted)", fontSize: "0.8125rem", fontStyle: "italic" },
  detailRow: { display: "flex", alignItems: "center", gap: "0.75rem" },
  detailLabel: { fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-muted)", minWidth: "3.5rem" },
  chips: { display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.35rem", alignItems: "center" },
  chip: { display: "inline-flex", alignItems: "center", gap: "0.4rem", background: "var(--ink-1)", border: "1px solid var(--rule)", borderRadius: "var(--r-md)", padding: "0.25rem 0.5rem", fontSize: "0.8125rem" },
  chipLink: { color: "var(--text-default)", textDecoration: "none" },
  chipX: { background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "1rem", lineHeight: 1, padding: 0 },
  addSetup: { fontSize: "0.8125rem", padding: "0.25rem 0.5rem", maxWidth: "16rem" },
  noRuns: { fontSize: "0.8125rem", color: "var(--text-muted)", fontStyle: "italic" },
  adminRow: { display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.6rem" },
  adminLink: { color: "var(--live)", textDecoration: "none", fontSize: "0.8125rem", fontWeight: 500 },
  adminBtn: { background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "0.8125rem", fontWeight: 500, padding: 0 },
  adminSep: { color: "var(--text-muted)", fontSize: "0.8125rem" },
};
